import axios from 'axios';
import { toast } from 'react-toastify';
import { auth } from './firebaseConfig';

// Determine the API URL based on the environment
const getApiUrl = () => {
  // Check for explicit environment variable first
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }
  
  // Auto-detect production environment
  if (import.meta.env.PROD || window.location.hostname.endsWith('artemerge.org')) {
    return 'https://art-emerge-4744deb5e11a.herokuapp.com/api';
  }
  
  // Default to local development
  return 'http://localhost:5051/api';
};

const baseURL = getApiUrl();

// Create axios instance with default config
export const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Log configuration in development
console.log('🔧 API Configuration:', {
  baseURL,
  env: import.meta.env.MODE,
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
  envVars: {
    VITE_API_URL: import.meta.env.VITE_API_URL
  }
});

// Request interceptor
api.interceptors.request.use(async (config) => {
  try {
    // In development mode, skip adding any auth tokens to use backend bypass
    if (import.meta.env.MODE === 'development') {
      console.log('🔧 API Interceptor: Development mode, skipping auth token');
      return config;
    }

    // In development mode without Firebase, use stored token
    if (import.meta.env.DEV && import.meta.env.VITE_FIREBASE_API_KEY === undefined) {
      const devToken = localStorage.getItem('token');
      if (devToken) {
        config.headers.Authorization = `Bearer ${devToken}`;
      }
      return config;
    }

    // A full page load can fire requests before Firebase restores the session.
    // Wait, then attach the token, instead of sending an anonymous request
    // that the app treats as a logout.
    if (auth?.authStateReady) {
      await auth.authStateReady();
    }

    const user = auth.currentUser;
    
    if (user) {
      const token = await user.getIdToken(false);
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Disable caching for Stripe endpoints
    if (config.url?.includes('/stripe/')) {
      config.headers['Cache-Control'] = 'no-cache';
      config.headers['Pragma'] = 'no-cache';
    }

    // Log request details in development
    if (import.meta.env.DEV) {
      console.log('🚀 API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        baseURL: config.baseURL,
        headers: config.headers,
        data: config.data
      });
    }
    
    return config;
  } catch (error) {
    console.error('Error in request interceptor:', error);
    return Promise.reject(error);
  }
});

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Log successful response in development
    if (import.meta.env.DEV) {
      console.log('✅ API Response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        headers: response.headers
      });
    }

    try {
      const method = (response.config?.method || 'GET').toUpperCase();
      const url = response.config?.url || '';

      // Only cache safe GET requests, skip uploads/Stripe and large POST bodies
      if (response.status === 304) {
        const cachedData = localStorage.getItem(`cache_${url}`);
        if (cachedData) {
          try { response.data = JSON.parse(cachedData); } catch (_) {}
        }
      } else if (method === 'GET' && !url.includes('/stripe/')) {
        try {
          localStorage.setItem(`cache_${url}`, JSON.stringify(response.data));
        } catch (e) {
          // Quota exceeded or serialization failures – degrade gracefully
          console.warn('⚠️ Skipping cache write for', url, e?.message);
          if (typeof window !== 'undefined' && /quota/i.test(e?.message || '')) {
            try { toast.warn('Device storage is low. Caching disabled for now.'); } catch (_) {}
          }
        }
      }
    } catch (cacheError) {
      // Never fail the request due to caching issues
      console.warn('⚠️ Cache interceptor error:', cacheError?.message);
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors
    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;

      try {
        if (auth?.authStateReady) {
          await auth.authStateReady();
        }
        const user = auth?.currentUser;
        if (!user) {
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Get fresh token
        const token = await user.getIdToken(true);  // Force refresh
        localStorage.setItem('token', token);
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Log error details
    console.error('❌ API Error:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      config: {
        method: error.config?.method,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        headers: error.config?.headers
      }
    });

    return Promise.reject(error);
  }
);

// Define API endpoints
export const endpoints = {
  auth: {
    login: '/auth/login',
    register: '/users/initialize',
    logout: '/auth/logout',
    verifyToken: '/auth/verify-token'
  },
  user: {
    profile: '/users/profile',
    update: '/users/profile',
    updateRole: '/users/update-role',
    sync: '/users/sync',
    initialize: '/users/initialize',
    initializeIfNeeded: '/users/initialize-if-needed',
    devSync: '/users/dev-sync',
    enhance: '/enhance/profile',
    uploadAvatar: '/users/avatar'
  },
  resume: {
    upload: '/resume/upload',
    get: '/resume'
  },
  artwork: {
    upload: '/artworks/upload',
    uploadLimits: '/artworks/upload-limits',
    list: '/artworks/list',
    myArtworks: '/artworks/my-artworks',
    get: (id) => `/artworks/${id}`,
    update: (id) => `/artworks/${id}`,
    delete: (id) => `/artworks/${id}`,
    publish: (id) => `/artworks/${id}/publish`,
    updateStatus: (id) => `/artworks/${id}/status`,
    updateSoldStatus: (id) => `/artworks/${id}/sold`
  },
  valuation: {
    estimate: '/valuation/estimate',
    history: '/valuation/history'
  },
  shipping: {
    packages: '/shipping/packages',
    rates: '/shipping/rates',
    createOrder: '/shipping/create-payment-order'
  },
  shopify: {
    products: '/shopify/products'
  },
  explore: {
    feed: '/explore/feed',
    like: '/explore/like',
    save: '/explore/save',
    exhibition: '/explore/exhibition',
    article: '/explore/article'
  },
  stripe: {
    createAccountLink: '/stripe/connect/account-link',
    accountStatus: '/stripe/account-status',
    commissionRate: '/stripe/commission-rate',
    payoutSchedule: '/stripe/payout-schedule',
    dashboard: '/stripe/dashboard',
    syncStatus: '/stripe/sync-account-status',
    createAccountSession: '/stripe/create-account-session'
  },
  exhibitions: {
    create: '/exhibitions',
    my: '/exhibitions/my',
    availableArtworks: '/exhibitions/available-artworks',
    update: (id) => `/exhibitions/${id}`,
    addArtwork: (id) => `/exhibitions/${id}/artworks`,
    removeArtwork: (id, artworkId) => `/exhibitions/${id}/artworks/${artworkId}`,
  },
  admin: {
    dashboardStats: '/admin/dashboard-stats',
    users: '/admin/users',
    userDetails: (id) => `/admin/users/${id}`,
    artistsActivity: '/admin/artists-activity',
    recentActivity: '/admin/recent-activity',
    artworks: '/admin/artworks',
    artistDetails: (id) => `/admin/artists/${id}`,
    userDetailsExpanded: (id) => `/admin/users/${id}/details`
  },
  artistProgress: {
    status: (email) => `/artist-progress/status/${encodeURIComponent(email)}`,
    test: '/artist-progress/test'
  },
};

export default endpoints;

// Test API connection
export const testApiConnection = async () => {
  try {
    const response = await api.get('/health');
    return response.status === 200;
  } catch (error) {
    console.error('API connection test failed:', error);
    return false;
  }
}; 